import { Fun, Option, Unit, deconstruct, none, some } from "widgets-for-react"
import { Right, Left, isLeft, isRight, mapRight } from "./sum"

export type Some<a> = Right<a>
export type None = Left<Unit>

export const isSome = <a>(o: Option<a>): o is { v: { kind: "r", v: a } } => isRight(o)
export const isNone = <a>(o: Option<a>): o is { v: { kind: "l", v: Unit } } => isLeft(o)

export const mapOption = <a,b>(f: (_:a) => b): (_: Option<a>) => Option<b> => mapRight(f)

/**
 * Returns the value of an Option if some, otherwise it will return the default value provided by the lazy function.
 * @param lazyDefault Lazy function that produces the default value
 * @param o The option containing the desired value
 * @returns The value of the option if some, otherwise the default value provided by the lazy function
 */
export const valueOrDefault = <a>(lazyDefault: () => a, o: Option<a>): a => deconstruct(lazyDefault, v => v, o)

export const valueOrExcept = <a>(o: Option<a>, message: string = "Option was `None` where `Some<X>` was expected") =>
  valueOrDefault(() => {throw new UnexpectedNoneException(message)}, o)

export const fromNullable = <a>(x: a | undefined | null): Option<a> => x !== undefined && x !== null ? some(x) : none() 

export const toNullable = <a>(o: Option<a>): a | undefined => valueOrDefault(() => undefined, o)

export const tryOrNone = <a, b>(f: Fun<a, b>) => (x: a): Option<b> => {
    try {
        return some(f(x))
    } catch (e) {
        return none()
    }
}

export const tryOrNull = <a, b>(f: Fun<a, b>) => (x: a) => toNullable(tryOrNone(f)(x))

class UnexpectedNoneException extends Error {
  constructor(message: string) {
    super(message)
  }
}
